import { useState, useEffect } from "react";
import DashboardPortal from "./DashboardPortal";
import { useNavigate } from "react-router-dom";
import NotificationSnackbar from "./NotificationBar";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { Chip, Tooltip } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import UnpublishedIcon from "@mui/icons-material/Unpublished";

(() => {
  if (!document.querySelector("[data-rc-font]")) {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href =
      "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;600&display=swap";
    l.setAttribute("data-rc-font", "");
    document.head.appendChild(l);
    const icons = document.createElement("link");
    icons.rel = "stylesheet";
    icons.href =
      "https://fonts.googleapis.com/icon?family=Material+Icons+Round";
    icons.setAttribute("data-rc-font", "");
    document.head.appendChild(icons);
  }
})();

const T = {
  blue900: "#0C2D6B",
  blue800: "#1044A3",
  blue700: "#1557C0",
  blue600: "#1A6FDB",
  blue500: "#2684F5",
  blue300: "#93C4FB",
  blue100: "#DBEAFE",
  blue50: "#EFF6FF",
  green700: "#166534",
  green500: "#22C55E",
  green100: "#DCFCE7",
  green50: "#F0FDF4",
  white: "#FFFFFF",
  gray50: "#F8FAFF",
  border: "rgba(26,111,219,0.14)",
  textSecondary: "#4A6082",
  textMuted: "#8FA5CC",
};

const EMPTY_FORM = {
  hiringCompany: "",
  hiringFor: "",
  quarter: "",
  sector: "",
  position: "",
  positionCategory: "",
  vacancyNumber: "",
  applications: "",
  applicationsMale: "",
  applicationsMaleYouth: "",
  applicationsMaleAdult: "",
  applicationsFemale: "",
  applicationsFemaleYouth: "",
  applicationsFemaleAdult: "",
  successfulCandidates: "",
  successfulMale: "",
  successfulMaleYouth: "",
  successfulMaleAdult: "",
  successfulFemale: "",
  successfulFemaleYouth: "",
  successfulFemaleAdult: "",
  publicationDate: "",
  offerDate: "",
};

const cellStyle = {
  fontSize: 11,
  whiteSpace: "nowrap",
  p: "4px 8px",
  fontFamily: "'Plus Jakarta Sans',sans-serif",
};
const cellStyleMuted = { ...cellStyle, color: T.textMuted };
const cellStyleBold = { ...cellStyle, fontWeight: 700 };
const cellStyleSuccess = { ...cellStyle, fontWeight: 700, color: T.green700 };

const ReportsTable = ({ reports, loading }) => {
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  if (loading) {
    return (
      <Typography sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        Loading reports...
      </Typography>
    );
  }

  if (!reports.length) {
    return (
      <Typography
        sx={{
          color: T.textMuted,
          fontFamily: "'Plus Jakarta Sans',sans-serif",
        }}
      >
        No recruitment reports available.
      </Typography>
    );
  }

  const paginatedReports = reports.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${T.border}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1400 }}>
          <TableHead sx={{ bgcolor: T.blue50 }}>
            <TableRow>
              {[
                "No",
                "Company",
                "Hiring For",
                "Qtr",
                "Sector",
                "Position",
                "Position Cat.",
                "Vacancies",
                "Pub. Date",
                "Total Apps",
                "Male Apps",
                "Male (Youth)",
                "Female Apps",
                "Female (Youth)",
                "Total Success",
                "Male Success",
                "Male S. (Youth)",
                "Female Success",
                "Female S. (Youth)",
                "Offer Date",
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    color: T.blue700,
                    fontWeight: 800,
                    fontSize: 9,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    p: "6px 8px",
                    fontFamily: "'Plus Jakarta Sans',sans-serif",
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedReports.map((row, i) => (
              <TableRow key={row.id || i} hover>
                <TableCell sx={cellStyleBold}>
                  {(page - 1) * rowsPerPage + i + 1}
                </TableCell>
                <TableCell sx={cellStyle}>{row.HiringCompany}</TableCell>
                <TableCell sx={cellStyle}>{row.hiringFor}</TableCell>
                <TableCell sx={cellStyle}>{row.Quarter}</TableCell>
                <TableCell sx={cellStyle}>{row.ISICEconomicSector}</TableCell>
                <TableCell sx={cellStyle}>{row.position}</TableCell>
                <TableCell sx={cellStyle}>{row.ISCOPositionCategory}</TableCell>
                <TableCell sx={cellStyle}>{row.vacancyNumber}</TableCell>
                <TableCell sx={cellStyle}>
                  {row.publicationDate?.split("T")[0]}
                </TableCell>
                <TableCell sx={cellStyleBold}>{row.applications}</TableCell>
                <TableCell sx={cellStyle}>{row.applicationsMale}</TableCell>
                <TableCell sx={cellStyleMuted}>
                  {row.applicationsMaleYouth}
                </TableCell>
                <TableCell sx={cellStyle}>{row.applicationsFemale}</TableCell>
                <TableCell sx={cellStyleMuted}>
                  {row.applicationsFemaleYouth}
                </TableCell>
                <TableCell sx={cellStyleSuccess}>
                  {row.successfulCandidates}
                </TableCell>
                <TableCell sx={cellStyle}>{row.successfulMale}</TableCell>
                <TableCell sx={cellStyleMuted}>
                  {row.successfulMaleYouth}
                </TableCell>
                <TableCell sx={cellStyle}>{row.successfulFemale}</TableCell>
                <TableCell sx={cellStyleMuted}>
                  {row.successfulFemaleYouth}
                </TableCell>
                <TableCell sx={cellStyle}>
                  {row.offerDate?.split("T")[0] || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {reports.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}>
          <Pagination
            count={Math.ceil(reports.length / rowsPerPage)}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
            size="medium"
            sx={{
              "& .MuiPaginationItem-root": { borderRadius: "999px" },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

const NewReportModal = ({ open, onClose, onSubmit, resetTrigger }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [sectors, setSectors] = useState([]);
  const [positionCategories, setPositionCategories] = useState([]);
  const [quartersList, setQuartersList] = useState([]);
  const [loadingTin, setLoadingTin] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [sectorSearch, setSectorSearch] = useState("");
  const [sectorOpen, setSectorOpen] = useState(false);
  const [positionSearch, setPositionSearch] = useState("");
  const [positionOpen, setPositionOpen] = useState(false);
  const filteredPositions = positionCategories.filter((p) =>
    p.name.toLowerCase().includes(positionSearch.toLowerCase()),
  );

  const handlePositionSelect = (position) => {
    setForm((prev) => ({ ...prev, positionCategory: position.id }));
    setPositionSearch(position.name);
    setPositionOpen(false);
  };

  const handlePositionInputChange = (e) => {
    setPositionSearch(e.target.value);
    setPositionOpen(true);
    if (!e.target.value) {
      setForm((prev) => ({ ...prev, positionCategory: "" }));
    }
  };
  const filteredSectors = sectors.filter((s) =>
    s.name.toLowerCase().includes(sectorSearch.toLowerCase()),
  );

  const handleSectorSelect = (sector) => {
    setForm((prev) => ({ ...prev, sector: sector.id }));
    setSectorSearch(sector.name);
    setSectorOpen(false);
  };

  const handleSectorInputChange = (e) => {
    setSectorSearch(e.target.value);
    setSectorOpen(true);
    if (!e.target.value) {
      setForm((prev) => ({ ...prev, sector: "" }));
    }
  };
  const [company, setCompany] = useState(() => {
    try {
      const companyData = localStorage.getItem("companyData");
      return companyData ? JSON.parse(companyData) : {};
    } catch (error) {
      console.error("Error parsing user data:", error);
      return {};
    }
  });
  const handleTinChange = async (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    setForm((prev) => ({
      ...prev,
      tin: value,
    }));

    if (value.length < 9) {
      setCompanyName("");
      return;
    }

    if (value.length === 9) {
      setLoadingTin(true);

      try {
        const res = await fetch("/api/support/verify-company", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ tin: value }),
        });

        const data = await res.json();
        if (data.success && data.data?.TaxPayerName) {
          setCompanyName(data.data.TaxPayerName);
          setForm((prev) => ({
            ...prev,
            hiringFor: data.data.TaxPayerName,
          }));
        } else {
          setCompanyName("");
        }
      } catch (error) {
        console.error("TIN verification failed:", error);
        setCompanyName("");
      } finally {
        setLoadingTin(false);
      }
    }
  };
  const handleCompanyNameClick = () => {
    if (!companyName) return;
    setForm((prev) => ({
      ...prev,
      tin: companyName,
      hiringFor: companyName,
    }));
    setCompanyName("");
  };
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const validate = () => {
    const errs = {};
    [
      "hiringFor",
      "sector",
      "position",
      "positionCategory",
      "applications",
      "successfulCandidates",
      "publicationDate",
    ].forEach((k) => {
      if (!form[k]) errs[k] = "Required";
    });

    const total = Number(form.applications || 0);
    const male = Number(form.applicationsMale || 0);
    const female = Number(form.applicationsFemale || 0);
    const maleYouth = Number(form.applicationsMaleYouth || 0);
    const femaleYouth = Number(form.applicationsFemaleYouth || 0);

    if (total > 0) {
      if (male + female !== total) {
        errs.applicationsMale = "Male + Female must equal total applications";
        errs.applicationsFemale = "Male + Female must equal total applications";
      }

      if (maleYouth > male) {
        errs.applicationsMaleYouth = "Youth cannot exceed male total";
      }

      if (femaleYouth > female) {
        errs.applicationsFemaleYouth = "Youth cannot exceed female total";
      }
    }
    const successfulTotal = Number(form.successfulCandidates || 0);
    const successfulMale = Number(form.successfulMale || 0);
    const successfulFemale = Number(form.successfulFemale || 0);
    const successfulMaleYouth = Number(form.successfulMaleYouth || 0);
    const successfulFemaleYouth = Number(form.successfulFemaleYouth || 0);
    if (successfulTotal > 0) {
      if (successfulMale + successfulFemale !== successfulTotal) {
        errs.successfulMale =
          "Male + Female must equal total successful candidates";
        errs.successfulFemale =
          "Male + Female must equal total successful candidates";
      }

      if (successfulMaleYouth > successfulMale) {
        errs.successfulMaleYouth =
          "Youth cannot exceed total successful male candidates";
      }

      if (successfulFemaleYouth > successfulFemale) {
        errs.successfulFemaleYouth =
          "Youth cannot exceed total successful female candidates";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await fetch("/api/support/positions-details");
        const data = await res.json();
        setSectors(data.economicSectors || []);
        setPositionCategories(data.positionCategories || []);
      } catch (err) {
        console.error("Error loading positions:", err);
      }
    };

    const fetchFiscalYears = async () => {
      try {
        const res = await fetch("/api/support/fiscalYears-quarters");
        const data = await res.json();
        const quartersFormatted = [];
        Object.keys(data).forEach((year) => {
          data[year].forEach((q) => {
            quartersFormatted.push(`${year}-${q}`);
          });
        });
        setQuartersList(quartersFormatted);
      } catch (err) {
        console.error("Error loading quarters:", err);
      }
    };

    fetchPositions();
    fetchFiscalYears();
  }, []);

  useEffect(() => {
    setForm(EMPTY_FORM);
    setErrors({});
  }, [resetTrigger]);

  const today = new Date().toISOString().split("T")[0];
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const maxDate = yesterday.toISOString().split("T")[0];

  const handleSubmit = () => {
    if (!company?.id) {
      console.error("Company ID missing");
      return;
    }
    if (validate()) {
      const payload = {
        ...form,
        hiringCompany: company?.id,

        applicationsMaleAdult:
          Number(form.applicationsMale || 0) -
          Number(form.applicationsMaleYouth || 0),

        applicationsFemaleAdult:
          Number(form.applicationsFemale || 0) -
          Number(form.applicationsFemaleYouth || 0),

        successfulMaleAdult:
          Number(form.successfulMale || 0) -
          Number(form.successfulMaleYouth || 0),

        successfulFemaleAdult:
          Number(form.successfulFemale || 0) -
          Number(form.successfulFemaleYouth || 0),
      };
      onSubmit(payload);
    }
  };
  const sections = [
    {
      label: "Position Information",
      icon: "person_outline",
      color: T.blue600,
      fields: (
        <>
          <TextField
            size="small"
            name="hiringCompany"
            label="Hiring Company"
            value={company?.name || ""}
            disabled
            fullWidth
            InputProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
                backgroundColor: "#f5f7fa",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
          />

          <TextField
            size="small"
            name="quarter"
            label="Quarter"
            value={form.quarter}
            onChange={handleChange}
            error={!!errors.quarter}
            helperText={errors.quarter}
            required
            select
            fullWidth
            InputProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
          >
            {quartersList.map((q) => (
              <MenuItem
                key={q}
                value={q}
                sx={{
                  fontFamily: "'Plus Jakarta Sans',sans-serif",
                  fontSize: 13,
                }}
              >
                {q}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ position: "relative" }}>
            <TextField
              size="small"
              name="tin"
              label="TIN of company you're hiring for"
              value={form.tin || ""}
              onChange={handleTinChange}
              inputProps={{ maxLength: 9 }}
              fullWidth
              InputProps={{
                sx: {
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                },
                endAdornment:
                  form.tin?.length === 9 ? (
                    loadingTin ? (
                      <CircularProgress size={15} />
                    ) : companyName ? (
                      <PublishedWithChangesIcon
                        sx={{ color: "success.main" }}
                      />
                    ) : (
                      <UnpublishedIcon sx={{ color: "error.main" }} />
                    )
                  ) : null,
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                },
              }}
            />
            {!loadingTin && companyName && form.tin?.length === 9 && (
              <Tooltip
                title="Click to use company name instead of TIN"
                placement="bottom-start"
              >
                <Chip
                  icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                  label={companyName}
                  onClick={handleCompanyNameClick}
                  size="small"
                  variant="outlined"
                  color="success"
                  sx={{
                    mt: 0.5,
                    maxWidth: "100%",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 11,
                    cursor: "pointer",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "&:hover": {
                      backgroundColor: "success.lighter",
                      borderColor: "success.main",
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ position: "relative", width: "100%" }}>
            <TextField
              size="small"
              label="ISIC Economic Sector"
              value={sectorSearch}
              onChange={handleSectorInputChange}
              onFocus={() => setSectorOpen(true)}
              onBlur={() => setTimeout(() => setSectorOpen(false), 200)}
              error={!!errors.sector}
              helperText={errors.sector}
              required
              fullWidth
              placeholder="Search sector..."
              InputProps={{
                sx: {
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                },
                endAdornment: sectorSearch ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSectorSearch("");
                      setSectorOpen(false);
                      setForm((prev) => ({ ...prev, sector: "" }));
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                ) : (
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 18,
                      color: "text.secondary",
                      transform: sectorOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                ),
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                },
              }}
            />
            {sectorOpen && filteredSectors.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1300,
                  mt: 0.5,
                  p: 1.5,
                  borderRadius: 2,
                  maxHeight: 260,
                  overflowY: "auto",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.8,
                  justifyContent: "center",
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "divider",
                    borderRadius: 4,
                  },
                }}
              >
                {filteredSectors.map((s) => (
                  <Chip
                    key={s.id}
                    label={s.name}
                    size="small"
                    onClick={() => handleSectorSelect(s)}
                    variant={form.sector === s.id ? "filled" : "outlined"}
                    color={form.sector === s.id ? "primary" : "default"}
                    sx={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 11,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      border: "1.5px solid",
                      borderColor:
                        form.sector === s.id ? "primary.main" : "divider",
                      backgroundColor:
                        form.sector === s.id ? "primary.main" : "transparent",
                      color: form.sector === s.id ? "white" : "text.primary",

                      "&:hover": {
                        backgroundColor: "primary.50",
                        borderColor: "primary.main",
                        color: "primary.dark",
                        transform: "scale(1.05)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      },

                      "& .MuiChip-label": {
                        px: 1.2,
                      },
                    }}
                  />
                ))}
                {filteredSectors.length === 0 && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      py: 1,
                    }}
                  >
                    No sectors found
                  </Typography>
                )}
              </Paper>
            )}
          </Box>
          <TextField
            size="small"
            name="position"
            label="Job Title"
            value={form.position}
            onChange={handleChange}
            error={!!errors.position}
            helperText={errors.position}
            required
            fullWidth
            InputProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
          />

          <Box sx={{ position: "relative", width: "100%" }}>
            <TextField
              size="small"
              label="ISCO Position Category"
              value={positionSearch}
              onChange={handlePositionInputChange}
              onFocus={() => setPositionOpen(true)}
              onBlur={() => setTimeout(() => setPositionOpen(false), 200)}
              error={!!errors.positionCategory}
              helperText={errors.positionCategory}
              required
              fullWidth
              placeholder="Search position..."
              InputProps={{
                sx: {
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                },
                endAdornment: positionSearch ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPositionSearch("");
                      setPositionOpen(false);
                      setForm((prev) => ({ ...prev, positionCategory: "" }));
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                ) : (
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 18,
                      color: "text.secondary",
                      transform: positionOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                ),
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                },
              }}
            />

            {positionOpen && filteredPositions.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1300,
                  mt: 0.5,
                  p: 1.5,
                  borderRadius: 2,
                  maxHeight: 260,
                  overflowY: "auto",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.8,
                  justifyContent: "center",
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "divider",
                    borderRadius: 4,
                  },
                }}
              >
                {filteredPositions.map((p) => (
                  <Chip
                    key={p.id}
                    label={p.name}
                    size="small"
                    onClick={() => handlePositionSelect(p)}
                    variant={
                      form.positionCategory === p.id ? "filled" : "outlined"
                    }
                    color={
                      form.positionCategory === p.id ? "primary" : "default"
                    }
                    sx={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 11,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      border: "1.5px solid",
                      borderColor:
                        form.positionCategory === p.id
                          ? "primary.main"
                          : "divider",
                      backgroundColor:
                        form.positionCategory === p.id
                          ? "primary.main"
                          : "transparent",
                      color:
                        form.positionCategory === p.id
                          ? "white"
                          : "text.primary",
                      "&:hover": {
                        backgroundColor: "primary.50",
                        borderColor: "primary.main",
                        color: "primary.dark",
                        transform: "scale(1.05)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      },
                      "& .MuiChip-label": {
                        px: 1.2,
                      },
                    }}
                  />
                ))}

                {filteredPositions.length === 0 && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      py: 1,
                    }}
                  >
                    No positions found
                  </Typography>
                )}
              </Paper>
            )}
          </Box>
        </>
      ),
    },
    {
      label: "Recruitment Details",
      icon: "assignment_turned_in",
      color: T.green600,
      fields: (
        <>
          <TextField
            size="small"
            name="publicationDate"
            label="Publication Date"
            type="date"
            value={form.publicationDate}
            onChange={handleChange}
            error={!!errors.publicationDate}
            helperText={errors.publicationDate}
            required
            fullWidth
            InputLabelProps={{
              shrink: true,
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            InputProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            inputProps={{ max: maxDate }}
          />
          <TextField
            size="small"
            name="vacancyNumber"
            label="Number of vacancies"
            type="number"
            value={form.vacancyNumber}
            onChange={handleChange}
            fullWidth
            InputProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            inputProps={{ min: 0 }}
          />

          <TextField
            size="small"
            name="applications"
            label="Number of applications"
            type="number"
            value={form.applications}
            onChange={handleChange}
            error={!!errors.applications}
            helperText={errors.applications}
            required
            fullWidth
            InputProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            inputProps={{ min: 0 }}
          />
          {Number(form.applications) > 0 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: T.textSecondary,
                  fontSize: 12,
                  fontFamily: "'Plus Jakarta Sans',sans-serif",
                }}
              >
                Number of Applications Breakdown ( Age of Youth 16-29)
              </Typography>

              {/* Male Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  name="applicationsMale"
                  label="Male"
                  type="number"
                  value={form.applicationsMale}
                  onChange={handleChange}
                  error={!!errors.applicationsMale}
                  helperText={errors.applicationsMale}
                  sx={{ width: 140 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  size="small"
                  name="applicationsMaleYouth"
                  label="Youth"
                  type="number"
                  value={form.applicationsMaleYouth}
                  onChange={handleChange}
                  error={!!errors.applicationsMaleYouth}
                  helperText={errors.applicationsMaleYouth}
                  sx={{ width: 120 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  inputProps={{ min: 0 }}
                />
              </Box>

              {/* Female Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  name="applicationsFemale"
                  label="Female"
                  type="number"
                  value={form.applicationsFemale}
                  onChange={handleChange}
                  error={!!errors.applicationsFemale}
                  helperText={errors.applicationsFemale}
                  sx={{ width: 140 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  size="small"
                  name="applicationsFemaleYouth"
                  label="Youth"
                  type="number"
                  value={form.applicationsFemaleYouth}
                  onChange={handleChange}
                  error={!!errors.applicationsFemaleYouth}
                  helperText={errors.applicationsFemaleYouth}
                  sx={{ width: 120 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  inputProps={{ min: 0 }}
                />
              </Box>
            </Box>
          )}
        </>
      ),
    },
    {
      label: "Recruitment Results",
      icon: "date_range",
      color: T.blue500,
      fields: (
        <>
          <TextField
            size="small"
            name="successfulCandidates"
            label="Total Successful"
            type="number"
            value={form.successfulCandidates}
            onChange={handleChange}
            error={!!errors.successfulCandidates}
            helperText={errors.successfulCandidates}
            required
            fullWidth
            InputProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            inputProps={{ min: 0 }}
          />

          {Number(form.successfulCandidates) > 0 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: T.textSecondary,
                  fontSize: 12,
                  fontFamily: "'Plus Jakarta Sans',sans-serif",
                }}
              >
                Successful Candidates Breakdown ( Age of Youth 16-29)
              </Typography>

              {/* Male */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  name="successfulMale"
                  label="Male"
                  type="number"
                  value={form.successfulMale}
                  onChange={handleChange}
                  error={!!errors.successfulMale}
                  helperText={errors.successfulMale}
                  sx={{ width: 140 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  size="small"
                  name="successfulMaleYouth"
                  label="Youth"
                  type="number"
                  value={form.successfulMaleYouth}
                  onChange={handleChange}
                  error={!!errors.successfulMaleYouth}
                  helperText={errors.successfulMaleYouth}
                  sx={{ width: 120 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  inputProps={{ min: 0 }}
                />
              </Box>

              {/* Female */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  name="successfulFemale"
                  label="Female"
                  type="number"
                  value={form.successfulFemale}
                  onChange={handleChange}
                  error={!!errors.successfulFemale}
                  helperText={errors.successfulFemale}
                  sx={{ width: 140 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  size="small"
                  name="successfulFemaleYouth"
                  label="Youth"
                  type="number"
                  value={form.successfulFemaleYouth}
                  onChange={handleChange}
                  error={!!errors.successfulFemaleYouth}
                  helperText={errors.successfulFemaleYouth}
                  sx={{ width: 120 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    },
                  }}
                  inputProps={{ min: 0 }}
                />
              </Box>
            </Box>
          )}
          <TextField
            size="small"
            name="offerDate"
            label="Offer Date (optional)"
            type="date"
            value={form.offerDate}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{
              shrink: true,
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            InputProps={{
              sx: {
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 13,
              },
            }}
            inputProps={{ max: today }}
          />
        </>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, border: `1px solid ${T.blue100}` } }}
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg,${T.blue700} 0%,${T.blue900} 100%)`,
          color: T.white,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontFamily: "'Playfair Display',serif" }}
          >
            New Recruitment Report
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.8, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
          >
            Fill in the details to log a recruitment entry
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: T.white,
            bgcolor: "rgba(255,255,255,0.15)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
          }}
        >
          <span className="material-icons-round">close</span>
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: T.gray50, p: { xs: 2, sm: 4 } }}>
        {sections.map((sec, idx) => (
          <Box
            key={sec.label}
            sx={{ mb: idx === sections.length - 1 ? 0 : 3.5 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <span
                className="material-icons-round"
                style={{ color: sec.color, fontSize: 20 }}
              >
                {sec.icon}
              </span>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: T.textSecondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  fontFamily: "'Plus Jakarta Sans',sans-serif",
                }}
              >
                {sec.label}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 1,
                  background: `linear-gradient(90deg,${T.border},transparent)`,
                }}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            >
              {sec.fields}
            </Box>
          </Box>
        ))}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 2.5,
          bgcolor: T.white,
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: T.textMuted,
            fontFamily: "'Plus Jakarta Sans',sans-serif",
          }}
        >
          <span style={{ color: T.blue600 }}></span>
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={onClose}
            sx={{
              color: T.blue600,
              fontFamily: "'Plus Jakarta Sans',sans-serif",
              fontWeight: 700,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<span className="material-icons-round">add</span>}
            sx={{
              background: `linear-gradient(135deg,${T.green500} 0%,${T.green700} 100%)`,
              fontFamily: "'Plus Jakarta Sans',sans-serif",
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              "&:hover": { filter: "brightness(1.1)" },
            }}
          >
            Submit Report
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const RecruitingPortal = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    const companyData = localStorage.getItem("companyData");
    if (!companyData) {
      navigate("/recruiting-company-welcome-page");
    }
  }, [navigate]);

  // SINGLE UNIFIED FETCH FUNCTION
  useEffect(() => {
    const loadReports = async () => {
      try {
        setLoading(true);

        const raw = localStorage.getItem("companyData");
        const company = raw ? JSON.parse(raw) : null;

        if (!company?.id) {
          setReports([]);
          return;
        }
        const res = await fetch(
          `/api/support/recruitment-reports?id=${company.id}`,
        );
        const data = await res.json();

        let fetchedReports = data.recordset || data;
        if (!Array.isArray(fetchedReports)) {
          fetchedReports = [];
        }

        setReports(fetchedReports);
      } catch (error) {
        console.error("Error fetching reports:", error);
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [resetTrigger]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const handleSubmit = async (formData) => {
    try {
      const res = await fetch("/api/support/hiring-company-reports", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to create report");
      }

      // Removed optimistic update to prevent UI flashing duplicates
      handleSnackbarOpen(data.message || "Report created successfully");

      // Trigger API refetch from DB
      setResetTrigger((prev) => prev + 1);

      setTimeout(() => {
        setShowModal(false);
        setActiveTab("table");
      }, 500);
    } catch (err) {
      console.error(err);
      handleSnackbarOpen(err.message || "Error creating report", "error");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(160deg,${T.gray50} 0%,${T.blue50} 50%,${T.green50} 100%)`,
        fontFamily: "'Plus Jakarta Sans',sans-serif",
        position: "relative",
      }}
    >
      {/* Background Orbs */}
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -160,
            right: -160,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background: `radial-gradient(circle,${T.blue100} 0%,transparent 70%)`,
            opacity: 0.7,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -120,
            left: -120,
            width: 440,
            height: 440,
            borderRadius: "50%",
            background: `radial-gradient(circle,${T.green100} 0%,transparent 70%)`,
            opacity: 0.8,
          }}
        />
      </Box>

      {/* ── Navbar ── */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          background: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(20px)",
          borderBottom: `1px solid ${T.border}`,
          height: 66,
          display: "flex",
          alignItems: "center",
          px: 4,
          justifyContent: "space-between",
          boxShadow: "0 2px 20px rgba(26,111,219,0.1)",
        }}
      >
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              background: `linear-gradient(135deg,${T.blue600},${T.blue800})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 14px rgba(26,111,219,0.4)",
            }}
          >
            <span
              className="material-icons-round"
              style={{ fontSize: 22, color: T.white }}
            >
              groups
            </span>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 800,
                color: T.blue900,
                fontFamily: "'Playfair Display',serif",
                lineHeight: 1.2,
              }}
            >
              RecruitConnect
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: T.textMuted,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontWeight: 600,
                fontFamily: "'Plus Jakarta Sans',sans-serif",
              }}
            >
              Reporting Portal
            </Typography>
          </Box>
        </Box>

        {/* Tab Navigation */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {[
            { key: "dashboard", icon: "dashboard", label: "Dashboard" },
            { key: "table", icon: "table_chart", label: "Reports" },
          ].map((n) => (
            <Button
              key={n.key}
              onClick={() => setActiveTab(n.key)}
              startIcon={
                <span className="material-icons-round" style={{ fontSize: 18 }}>
                  {n.icon}
                </span>
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                borderRadius: 2.5,
                px: 2.5,
                py: 1,
                color: activeTab === n.key ? T.blue700 : T.textSecondary,
                bgcolor: activeTab === n.key ? T.blue100 : "transparent",
                boxShadow:
                  activeTab === n.key
                    ? `inset 0 0 0 1.5px ${T.blue300}`
                    : "none",
                "&:hover": {
                  bgcolor: activeTab === n.key ? T.blue100 : T.blue50,
                  color: T.blue600,
                },
              }}
            >
              {n.label}
            </Button>
          ))}
        </Box>

        <Button
          onClick={openModal}
          variant="contained"
          startIcon={<span className="material-icons-round">add</span>}
          sx={{
            background: `linear-gradient(135deg,${T.blue600} 0%,${T.blue800} 100%)`,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2.5,
            fontFamily: "'Plus Jakarta Sans',sans-serif",
            boxShadow: "0 4px 16px rgba(26,111,219,0.35)",
            "&:hover": {
              background: `linear-gradient(135deg,${T.blue700} 0%,${T.blue900} 100%)`,
            },
          }}
        >
          New Report
        </Button>
      </Box>
      <Box
        sx={{
          maxWidth: "100%",
          margin: "0 auto",
          padding: "32px 28px 64px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {activeTab === "dashboard" && <DashboardPortal reports={reports} />}
        {activeTab === "table" && (
          <ReportsTable reports={reports} loading={loading} />
        )}
      </Box>

      <NewReportModal
        open={showModal}
        onClose={closeModal}
        onSubmit={handleSubmit}
        resetTrigger={resetTrigger}
      />

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default RecruitingPortal;
